// The workspace's left column: a tabbed sidebar so the editor + preview stay
// central while outline / sources / figures / bibliography / tasks are reachable.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReportDocument } from "@/api/report";
import { OutlinePanel } from "./outline-panel";
import { LookupIndexPanel } from "./lookup-index-panel";
import { FigureLibraryPanel } from "./figure-library-panel";
import { BibEditorPanel } from "./bib-editor-panel";
import { TasksPanel } from "./tasks-panel";

type Tab = "outline" | "sources" | "figures" | "bib" | "tasks";

export function ReportSidebar({
  projectId,
  report,
  activeSectionIndex,
  onChanged,
  onDocChanged,
  onGoToSection,
  onEditSection,
  onInsertCite,
  labelReqs,
  onLabelReqs,
}: {
  projectId: string;
  report: ReportDocument;
  activeSectionIndex: number;
  onChanged: () => void;
  onDocChanged: () => void;
  onGoToSection: (index: number) => void;
  onEditSection: (index: number, title: string) => void;
  onInsertCite: (key: string) => void;
  labelReqs?: Record<string, string>;
  onLabelReqs?: (next: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("outline");

  const tabs: { key: Tab; label: string }[] = [
    { key: "outline", label: t("Outline") },
    { key: "sources", label: t("Sources") },
    { key: "figures", label: t("Figures") },
    { key: "bib", label: t("Refs") },
    { key: "tasks", label: t("Tasks") },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex gap-1 border-b border-black/5 p-1">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            onClick={() => setTab(tb.key)}
            className={`flex-1 rounded-md px-1.5 py-1 text-xs font-medium ${
              tab === tb.key ? "bg-brand-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1">
        {tab === "outline" && (
          <OutlinePanel
            outline={report.outline}
            activeIndex={activeSectionIndex}
            onGoToSection={onGoToSection}
            onEditSection={onEditSection}
          />
        )}
        {tab === "sources" && (
          <LookupIndexPanel
            surveys={report.file_surveys}
            projectId={projectId}
            onChanged={onChanged}
          />
        )}
        {tab === "figures" && (
          <FigureLibraryPanel
            projectId={projectId}
            assets={report.assets}
            files={report.file_surveys}
            onChanged={onChanged}
            onInserted={onDocChanged}
            labelReqs={labelReqs}
            onLabelReqs={onLabelReqs}
          />
        )}
        {tab === "bib" && <BibEditorPanel projectId={projectId} onInsertCite={onInsertCite} />}
        {tab === "tasks" && (
          <TasksPanel projectId={projectId} files={report.file_surveys} onSolved={onDocChanged} />
        )}
      </div>
    </div>
  );
}
